import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { IntelligemsAppScopeList } from "@intelligems/ig-types";
import { useHasRequiredShopifyScopes } from "@src/components/LimitAccess/hooks";
import { useDispatch, useSelector } from "@src/store";

import { setDialog } from "../store/slices/global";

export function useQueryParams() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), []);
}

export function useQueryParam(param: string): string | null {
  return useMemo(() => {
    const params = new URLSearchParams(document.location.search);
    return params.get(param);
  }, [param]);
}

export const useMaybeShowMissingShopifyScopesDialogAtLogin = () => {
  const snoozeShopifyScopesDialog = useSelector(
    (state) => state.appSettings.snoozeShopifyScopesDialog
  );
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes([
    ...IntelligemsAppScopeList,
  ]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (hasRequiredShopifyScopes !== undefined) {
      if (hasRequiredShopifyScopes) {
        dispatch(setDialog({ dialog: "missingShopifyScope", open: false }));
      } else {
        if (
          !snoozeShopifyScopesDialog ||
          (snoozeShopifyScopesDialog &&
            DateTime.fromSeconds(snoozeShopifyScopesDialog).diffNow("days")
              .days > 2)
        ) {
          dispatch(
            setDialog({
              dialog: "missingShopifyScope",
              open: true,
              props: { initial: true },
            })
          );
        } else {
          dispatch(setDialog({ dialog: "missingShopifyScope", open: false }));
        }
      }
    }
  }, [hasRequiredShopifyScopes]);
};

export const useLazyUpdateValue = (
  value: string,
  onChange: (value: string) => void,
  time = 500
) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, time);

    return () => {
      clearTimeout(timeout);
    };
  }, [value]);
};

export const useElement = <T extends Element>() => {
  const [element, setElement] = useState<T | undefined>(undefined);
  const ref = (el: T) => {
    if (el !== element) {
      setTimeout(() => setElement(el), 0);
    }
  };
  return { ref, element };
};

export const useOnResize = <T extends HTMLElement>(
  onResize: (el: T) => void
) => {
  const data = useRef<{ el: T; observer: ResizeObserver }>(undefined);
  return (el: T) => {
    if (el !== data.current?.el) {
      data.current?.observer.disconnect();
      if (el) {
        const observer = new ResizeObserver(() => onResize(el));
        observer.observe(el);
        onResize(el);
        data.current = { el, observer };
      } else {
        data.current = undefined;
      }
    }
  };
};
